/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */

// i18n must be imported before other components
import './i18n';

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Import components
import LandingPage from './components/LandingPage';
import SignupPage from './components/SignupPage';
import OnboardingWizard from './components/OnboardingWizard';
import DemoPage from './components/DemoPage';
import Dashboard from './components/Dashboard';
import TenantLogin from './components/TenantLogin';
import PropertyManagerLogin from './components/PropertyManagerLogin';
import PropertyManagerDashboard from './components/PropertyManagerDashboard';
import LandlordLogin from './components/LandlordLogin';
import HOAAdminLogin from './components/HOAAdminLogin';
import HOAOwnerLogin from './components/HOAOwnerLogin';
import HOAOwnerRegister from './components/HOAOwnerRegister';
import HOAOwnerDashboard from './components/HOAOwnerDashboard';
import TenantDashboard from './components/TenantDashboard';
import DashboardRouter from './components/DashboardRouter';
import FAQPage from './components/FAQPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ReferralPartnerSignup from './components/ReferralPartnerSignup';
import ReferralDashboard from './components/ReferralDashboard';
import ReferralDashboardDemo from './components/ReferralDashboardDemo';
import ReferralLogin from './components/ReferralLogin';
import TermsConditions from './components/TermsConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import PrivateRoute from './components/PrivateRoute';
import SupportChat from './components/SupportChat';
import BillingSuccess from './components/BillingSuccess';
import VacationRentalsDashboard from './components/VacationRentalsDashboard';
import VacationRentalsSearch from './components/VacationRentalsSearch';
import VacationPropertyDetail from './components/VacationPropertyDetail';
import VacationRentalLogin from './components/VacationRentalLogin';
import { GuestLogin, GuestRegister } from './components/GuestAuth';
import FreeMaintenanceLog from './components/FreeMaintenanceLog';
import FreeLeaseTracker from './components/FreeLeaseTracker';
import KnowledgeBase from './components/KnowledgeBase';

// Real Estate Financing Module
import FinancingLeadForm from './components/financing/FinancingLeadForm';
import BankingPartnerLogin from './components/financing/BankingPartnerLogin';
import BankingPartnerDashboard from './components/financing/BankingPartnerDashboard';

// Embed Booking Widget
import EmbedBookingWidget from './components/EmbedBookingWidget';

// Leasing Hub
import RentalMarketplace from './components/RentalMarketplace';

// Vendor Portal
import VendorLogin from './components/VendorLogin';
import VendorPortal from './components/VendorPortal';
import VendorActivate from './components/VendorActivate';
import StaffActivate from './components/StaffActivate';

import { Toaster } from '@/components/ui/sonner';
import { trackPageView} from './utils/analytics';

// Page View Tracker Component
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      // Validate token and get user info
      setUser({ token });
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/help" element={<FAQPage />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/become-partner" element={<ReferralPartnerSignup />} />
          <Route path="/referral-partner" element={<ReferralPartnerSignup />} />
          <Route path="/referral/login" element={<ReferralLogin />} />
          {/* Demo page secured - requires admin login */}
          <Route path="/referral/demo" element={<PrivateRoute><ReferralDashboardDemo /></PrivateRoute>} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/billing/cancel" element={<Navigate to="/dashboard" replace />} />
          <Route path="/referral/dashboard" element={<PrivateRoute><ReferralDashboard /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />
          {/* Admin Routes - Direct access only, not publicly listed */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
          <Route path="/landlord/login" element={<LandlordLogin />} />
          <Route path="/landlord/dashboard" element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />
          <Route path="/property-manager/login" element={<PropertyManagerLogin />} />
          <Route path="/property-manager/dashboard" element={<PrivateRoute><PropertyManagerDashboard /></PrivateRoute>} />
          
          {/* Vacation Rentals Manager Login & Dashboard */}
          <Route path="/vacation-rental/login" element={<VacationRentalLogin />} />
          <Route path="/vacation-rentals/login" element={<VacationRentalLogin />} />
          <Route path="/vacation-rentals" element={<PrivateRoute><VacationRentalsDashboard /></PrivateRoute>} />
          
          {/* Public Vacation Rentals Portal (For Guests) */}
          <Route path="/vacation-rentals/search" element={<VacationRentalsSearch />} />
          <Route path="/vacation-rentals/property/:propertyId" element={<VacationPropertyDetail />} />
          
          {/* Leasing Hub - For landlords and HOA owners to list vacant units */}
          <Route path="/rentals" element={<RentalMarketplace />} />
          <Route path="/rental-marketplace" element={<RentalMarketplace />} />
          
          {/* Guest Authentication */}
          <Route path="/guest/login" element={<GuestLogin />} />
          <Route path="/guest/register" element={<GuestRegister />} />
          {/* Guest Dashboard redirect - no dashboard exists, send to search */}
          <Route path="/guest/dashboard" element={<Navigate to="/vacation-rentals/search" replace />} />
          
          {/* Knowledge Base / Help Center */}
          <Route path="/kb" element={<KnowledgeBase />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          
          {/* Free Tools (Lead Magnets) */}
          <Route path="/free-tools/maintenance-log" element={<FreeMaintenanceLog />} />
          <Route path="/free-tools/lease-tracker" element={<FreeLeaseTracker />} />
          
          {/* Real Estate Financing Module - Wellington Woods & Associates */}
          <Route path="/financing" element={<FinancingLeadForm />} />
          <Route path="/banking-partner/login" element={<BankingPartnerLogin />} />
          <Route path="/banking-partner/dashboard" element={<PrivateRoute><BankingPartnerDashboard /></PrivateRoute>} />
          
          {/* Embeddable Booking Widget for Vacation Rentals */}
          <Route path="/embed/booking/:ownerId" element={<EmbedBookingWidget />} />
          
          {/* Vendor Portal */}
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/vendor/register" element={<VendorLogin />} />
          <Route path="/vendor/activate" element={<VendorActivate />} />
          <Route path="/staff/activate" element={<StaffActivate />} />
          <Route path="/vendor/dashboard" element={<VendorPortal />} />
          
          <Route path="/hoa-admin/login" element={<HOAAdminLogin />} />
          <Route path="/hoa-admin/dashboard" element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />
          <Route path="/hoa-owner/login" element={<HOAOwnerLogin />} />
          <Route path="/hoa-owner/register" element={<HOAOwnerRegister />} />
          <Route path="/hoa-owner/dashboard" element={<PrivateRoute><HOAOwnerDashboard /></PrivateRoute>} />
          <Route path="/tenant/login" element={<TenantLogin />} />
          <Route path="/tenant/dashboard" element={<PrivateRoute><TenantDashboard /></PrivateRoute>} />
        </Routes>
        <SupportChat />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
